import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';

/**
 * frontend/src/components/UnsavedChanges.jsx
 *
 * Two things, kept together because they are two halves of one idea:
 *
 *   useEditableRecord  — a detail page's form state, with dirty tracking
 *   UnsavedGuard       — stops navigation away from unsaved edits
 *
 * Why the guard is not `useBlocker`: that needs a data router
 * (`createBrowserRouter`). This app uses `<BrowserRouter><Routes>`, where it is
 * unavailable. So the guard intercepts at the only place in-app navigation
 * actually starts — the sidebar links — and `beforeunload` covers closing the
 * tab. A `<Link>` added elsewhere is not covered; `confirmLeave()` is exported
 * so such a link can call it.
 */

const GuardCtx = createContext(null);

export function UnsavedGuardProvider({ children }) {
  // A ref, not state: changing it must not re-render the whole app on
  // every keystroke in a form.
  const dirtyRef = useRef(false);
  const [ask, setAsk] = useState(null);

  const setDirty = useCallback((v) => { dirtyRef.current = !!v; }, []);

  /** Resolves true if it is safe to leave. Awaits the person if it is not. */
  const confirmLeave = useCallback(() => {
    if (!dirtyRef.current) return Promise.resolve(true);
    return new Promise(resolve => setAsk({ resolve }));
  }, []);

  const answer = (leave) => {
    if (leave) dirtyRef.current = false;   // they chose to discard
    ask?.resolve(leave);
    setAsk(null);
  };

  const value = useMemo(() => ({ setDirty, confirmLeave }), [setDirty, confirmLeave]);

  return (
    <GuardCtx.Provider value={value}>
      {children}
      {ask && (
        <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => answer(false)} />
          <div className="relative w-full max-w-md rounded-xl shadow-2xl p-5"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <p className="font-semibold mb-1" style={{ color: 'var(--text)' }}>
              You have unsaved changes
            </p>
            <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>
              Leaving this page will discard them.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => answer(false)} className="btn-secondary">Stay on this page</button>
              <button onClick={() => answer(true)} className="btn-danger" autoFocus>Discard and leave</button>
            </div>
          </div>
        </div>
      )}
    </GuardCtx.Provider>
  );
}

/** Falls back to allowing navigation, so a missing provider never traps anyone. */
const NO_GUARD = Object.freeze({ setDirty: () => {}, confirmLeave: () => Promise.resolve(true) });
export const useUnsavedGuard = () => useContext(GuardCtx) || NO_GUARD;

/**
 * A detail page's editable record.
 *
 *   const rec = useEditableRecord(client);
 *   <input value={rec.form.name || ''} onChange={e => rec.set({ name: e.target.value })} />
 *   {rec.dirty && <SaveBar {...rec} onSave={() => save(rec.patch())} />}
 *
 * `record` is the loaded row. When it changes — after a save, or a reload —
 * the form resets to it.
 */
export function useEditableRecord(record) {
  const [form, setForm] = useState(record || {});
  const { setDirty } = useUnsavedGuard();

  // Reset when a genuinely different version of the record arrives.
  const stamp = record ? `${record.id ?? ''}|${record.updated_at ?? ''}` : '';
  useEffect(() => { setForm(record || {}); }, [stamp]);   // eslint-disable-line

  const dirtyFields = useMemo(() => {
    if (!record) return [];
    return Object.keys(form).filter(k => {
      const a = form[k], b = record[k];
      if (a == null && b == null) return false;
      // `typeof null` is 'object', so null must be ruled out BEFORE this or a
      // stored null compared against an emptied input reports a change nobody
      // made — a save bar that will not go away.
      const aObj = a !== null && typeof a === 'object';
      const bObj = b !== null && typeof b === 'object';
      if (aObj || bObj) return JSON.stringify(a ?? null) !== JSON.stringify(b ?? null);
      // An empty input and a stored null are the same thing to a person.
      return String(a ?? '') !== String(b ?? '');
    });
  }, [form, record]);

  const dirty = dirtyFields.length > 0;

  // Tell the navigation guard, and warn on tab close.
  useEffect(() => {
    setDirty(dirty);
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => { window.removeEventListener('beforeunload', warn); setDirty(false); };
  }, [dirty, setDirty]);

  const set = useCallback((patch) => setForm(f => ({ ...f, ...patch })), []);
  const discard = useCallback(() => setForm(record || {}), [record]);

  /** Only what changed — so two people editing different fields do not collide. */
  const patch = useCallback(
    () => Object.fromEntries(dirtyFields.map(k => [k, form[k]])),
    [dirtyFields, form]);

  return { form, set, discard, dirty, dirtyFields, patch, setForm };
}

/** The bar every converted detail page shows. One definition, one behaviour. */
export function SaveBar({ dirtyFields, discard, onSave, saving }) {
  if (!dirtyFields?.length) return null;
  const n = dirtyFields.length;
  return (
    <div className="sticky bottom-0 z-30 mt-4 rounded-xl shadow-lg flex items-center justify-between gap-4 px-4 py-3"
      style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderLeft: '4px solid #FFC849' }}>
      <p className="text-sm" style={{ color: 'var(--text)' }}>
        <strong>{n}</strong> unsaved change{n === 1 ? '' : 's'}
        <span className="ml-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
          {dirtyFields.slice(0, 4).map(f => f.replace(/_/g, ' ')).join(', ')}
          {n > 4 ? ` and ${n - 4} more` : ''}
        </span>
      </p>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button onClick={discard} disabled={saving} className="btn-secondary text-sm">Discard</button>
        <button onClick={onSave} disabled={saving} className="btn-primary text-sm flex items-center gap-2">
          {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin" />}
          Save changes
        </button>
      </div>
    </div>
  );
}
